import * as React from "react";
import { Box, Button, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import type { ShotBookmark } from "./shotBookmarks";

export function ShotBookmarksPopup(props: {
  open: boolean;
  bookmarks: ShotBookmark[];
  onClose: () => void;
  onWatch: (bookmark: ShotBookmark) => void;
  onOpenLab: (bookmark: ShotBookmark) => void;
  onUpdateDescription: (bookmarkId: string, description: string) => void;
  onDelete: (bookmarkId: string) => void;
}) {
  const {
    open,
    bookmarks,
    onClose,
    onWatch,
    onOpenLab,
    onUpdateDescription,
    onDelete,
  } = props;
  const [draftDescriptions, setDraftDescriptions] = React.useState<Record<string, string>>(
    {},
  );

  React.useEffect(() => {
    if (!open) return;
    setDraftDescriptions(
      Object.fromEntries(bookmarks.map((bookmark) => [bookmark.id, bookmark.description])),
    );
  }, [bookmarks, open]);

  if (!open) return null;

  const sortedBookmarks = [...bookmarks].sort(
    (a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt),
  );

  return (
    <Box
      data-demo="shot-bookmarks-popup"
      onPointerDown={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 14000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "rgba(0,0,0,0.58)",
      }}
    >
      <Box
        sx={{
          width: "min(760px, calc(100vw - 32px))",
          maxHeight: "min(720px, calc(100vh - 32px))",
          display: "flex",
          flexDirection: "column",
          border: "1px solid rgba(255,255,255,0.22)",
          borderRadius: 1,
          bgcolor: "rgba(10,10,10,0.98)",
          color: "#fff",
          boxShadow: "0 22px 70px rgba(0,0,0,0.7)",
        }}
      >
        <Box
          sx={{
            px: 1.4,
            py: 1,
            display: "flex",
            alignItems: "center",
            borderBottom: "1px solid rgba(255,255,255,0.12)",
          }}
        >
          <Box sx={{ fontWeight: 900, fontSize: 16 }}>
            Shot bookmarks
            <Box
              component="span"
              sx={{ color: "rgba(255,255,255,0.58)", fontSize: 13, ml: 0.7 }}
            >
              {bookmarks.length}
            </Box>
          </Box>
          <Box sx={{ flex: 1 }} />
          <IconButton
            size="small"
            data-demo="shot-bookmarks-close"
            onClick={onClose}
            sx={{ color: "#ddd" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ overflowY: "auto", p: 1.25 }}>
          {sortedBookmarks.length === 0 ? (
            <Box
              sx={{
                px: 1,
                py: 3,
                color: "rgba(255,255,255,0.68)",
                textAlign: "center",
                fontWeight: 700,
              }}
            >
              No bookmarked shots yet.
            </Box>
          ) : null}

          {sortedBookmarks.map((bookmark) => {
            const draft = draftDescriptions[bookmark.id] ?? bookmark.description;
            const dirty = draft !== bookmark.description;
            const hasLabTarget = bookmark.lab?.target != null;
            return (
              <Box
                key={bookmark.id}
                data-demo="shot-bookmark-row"
                sx={{
                  display: "grid",
                  gridTemplateColumns: "150px 1fr 220px",
                  gap: 1.1,
                  alignItems: "start",
                  px: 1,
                  py: 1,
                  mb: 0.75,
                  border: "1px solid rgba(255,255,255,0.12)",
                  borderRadius: 1,
                  bgcolor: "rgba(255,255,255,0.045)",
                  "@media (max-width: 760px)": {
                    gridTemplateColumns: "1fr",
                  },
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ fontWeight: 900, fontSize: 13.5 }}>
                    Rally {bookmark.rallyIndex + 1} · Shot {bookmark.shotNumber}
                  </Box>
                  <Box sx={{ mt: 0.35, color: "rgba(255,255,255,0.62)", fontSize: 12 }}>
                    {bookmark.playerName
                      ? bookmark.playerName
                      : bookmark.playerId != null
                        ? `Player ${bookmark.playerId}`
                        : "Player unknown"}
                  </Box>
                  <Box sx={{ mt: 0.2, color: "rgba(255,255,255,0.48)", fontSize: 12 }}>
                    {bookmark.timeSec == null ? "time n/a" : `${bookmark.timeSec.toFixed(2)}s`}
                    {hasLabTarget ? " · target saved" : ""}
                  </Box>
                </Box>

                <Box>
                  <Box
                    component="textarea"
                    value={draft}
                    placeholder="Reason for bookmarking"
                    onChange={(event) =>
                      setDraftDescriptions((current) => ({
                        ...current,
                        [bookmark.id]: event.target.value,
                      }))
                    }
                    onBlur={() => {
                      if (dirty) onUpdateDescription(bookmark.id, draft);
                    }}
                    sx={{
                      width: "100%",
                      minHeight: 62,
                      resize: "vertical",
                      boxSizing: "border-box",
                      border: "1px solid rgba(255,255,255,0.18)",
                      borderRadius: 0.75,
                      bgcolor: "rgba(0,0,0,0.45)",
                      color: "#fff",
                      px: 1,
                      py: 0.8,
                      font: "inherit",
                      fontSize: 13,
                      outline: "none",
                    }}
                  />
                </Box>

                <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", justifyContent: "flex-end" }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => onWatch(bookmark)}
                    sx={{ textTransform: "none", minHeight: 28 }}
                  >
                    Watch
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => onOpenLab(bookmark)}
                    sx={{ textTransform: "none", minHeight: 28 }}
                  >
                    Target Lab
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => onDelete(bookmark.id)}
                    sx={{ textTransform: "none", minHeight: 28 }}
                  >
                    Delete
                  </Button>
                  {dirty ? (
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => onUpdateDescription(bookmark.id, draft)}
                      sx={{ textTransform: "none", minHeight: 28 }}
                    >
                      Save note
                    </Button>
                  ) : null}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
